<script>
    import { appStateStore, finalNonconformityStore, fetchFinalNonconformityData } from "./store.js";
    import NavBar from "../../components/NavBar.svelte";
    import FinalNonconformitySearch from "./FinalNonconformitySearch.svelte";
    import FinalNonconformityList from "./FinalNonconformityList.svelte";
    import FinalNonconformityDetail from "./FinalNonconformityDetail.svelte";
    import BarcodeScannerModal from "../../components/BarcodeScannerModal.svelte";
    import { onMount, tick } from "svelte";

    let isScanning = false;
    let scannerRef;
    import { Preferences } from "@capacitor/preferences";
    import * as api from "../../utiles/api.js";
    import toast from "svelte-french-toast"; // 추가: toast 알림창

    let title = "최종부적합조치관리";
    let session_bplc_code = "01";

    // 반응형 변수
    $: isListView = $appStateStore.currentView === 'list';
    $: isDetailView = $appStateStore.currentView === 'detail';

    onMount(async () => {
        const { value } = await Preferences.get({ key: "retechInfo" });
        if (value) {
            const userData = JSON.parse(value);
            session_bplc_code = userData.bplc_code || "01";
        }

        // 이미 데이터가 있으면 재호출 안함 (메뉴에서 선행 로딩된 경우 대비)
        if ($finalNonconformityStore.list.length === 0) {
            getData();
        }
    });

    async function getData() {
        await fetchFinalNonconformityData(session_bplc_code);
    }

    function handleMenuReset() {
        appStateStore.set({ currentView: 'list', selectedId: null });
    }

    function handleReset() {
        const today = new Date();
        const fourMonthsAgo = new Date();
        fourMonthsAgo.setMonth(today.getMonth() - 4);

        const formatDate = (date) => date.toISOString().split('T')[0];

        finalNonconformityStore.set({
            list: [],
            selectedItem: null,
            details: [],
            defectPhotos: [],
            actionDetails: [],
            actionPhotos: [],
            search: {
                prdctn_plan_no: '',
                item_code: '',
                item_nm: '',
                startDate: '2025-01-01',
                endDate: '2026-12-31',
                inspct_empno: '',
                search_korean_nm: '',
                isExpanded: true
            },
            scrollPos: 0
        });

        // 뷰 상태도 리스트로 초기화
        appStateStore.set({ currentView: 'list', selectedId: null });

        tick().then(() => getData());
    }

    let barcodeBuffer = "";
    let lastKeyTime = 0;
    let isScanner = false;

    // 추가: 바코드 리딩 시 최신 회차(ODR) 상세 페이지로 이동
    // 💡 반환값: 카메라 스캐너 판정용 { success, message }
    async function handleBarcodeScan(barcode) {
        const list = $finalNonconformityStore.list;
        const matches = list.filter((item) => item.lot_no === barcode);

        if (matches.length > 0) {
            // 회차(ODR) 내림차순 정렬하여 가장 높은(최신) 회차를 고릅니다.
            matches.sort((a, b) => (b.odr || 0) - (a.odr || 0));
            const latestItem = matches[0];

            const loadingToast = toast.loading("바코드 상세 내역 조회 중...");
            try {
                finalNonconformityStore.update((s) => ({ ...s, selectedItem: latestItem }));

                const params = new URLSearchParams({
                    bplc_code: latestItem.bplc_code || "01",
                    prdctn_plan_no: latestItem.prdctn_plan_no,
                    odr: latestItem.odr,
                });

                const response = await api.get(
                    `final-nonconformity/detail?${params.toString()}`,
                );
                if (response && response.data) {
                    finalNonconformityStore.update((s) => ({
                        ...s,
                        details: response.data,
                    }));
                }

                toast.dismiss(loadingToast);
                appStateStore.set({ currentView: "detail", selectedId: latestItem.id });
                return { success: true, message: `${latestItem.lot_no} - ${latestItem.odr}회차` };
            } catch (err) {
                console.error("Error barcode detail:", err);
                toast.dismiss(loadingToast);
                return { success: false, message: "상세 내역 조회 중 오류가 발생했습니다." };
            }
        } else {
            return { success: false, message: "부적합 리스트에서 일치하는 LOT NO를 찾을 수 없습니다." };
        }
    }

    function handleWindowKeydown(e) {
        if ($appStateStore.currentView !== "list") return;

        const currentTime = Date.now();
        const timeDiff = currentTime - lastKeyTime;
        lastKeyTime = currentTime;

        const isEnter = e.key === "Enter" || e.keyCode === 13;

        if (timeDiff > 50) {
            barcodeBuffer = "";
            isScanner = false;
        }

        if (isEnter) {
            if (barcodeBuffer.length > 3 && (timeDiff <= 50 || isScanner)) {
                e.preventDefault();
                e.stopPropagation();
                if (document.activeElement && typeof document.activeElement.blur === 'function') {
                    document.activeElement.blur();
                }

                handleBarcodeScan(barcodeBuffer).then((result) => {
                    if (result.success) {
                        toast.success(result.message);
                    } else {
                        toast.error(result.message);
                    }
                });
                barcodeBuffer = "";
                isScanner = false;
            }
            return;
        }

        if (e.key.length === 1) {
            barcodeBuffer += e.key;
            if (timeDiff <= 50) {
                isScanner = true;
                if (document.activeElement && document.activeElement.tagName === 'BUTTON') {
                    document.activeElement.blur();
                }
            }
        }
    }
</script>

<svelte:window on:keydown|capture={handleWindowKeydown} />

<div class="page-container">
    <NavBar activeMenu={title} on:reset={handleReset} on:menuReset={handleMenuReset} />
    
    <div class="content-wrapper">
        {#if isListView}
            <div class="list-container-wrapper">
                <FinalNonconformitySearch {getData} />
                <main class="main-layout">
                    <FinalNonconformityList on:openScanner={() => isScanning = true} />
                </main>
            </div>
        {/if}

        {#if isDetailView}
            <div class="detail-container-wrapper">
                <FinalNonconformityDetail />
            </div>
        {/if}
    </div>

    {#if isScanning}
        <BarcodeScannerModal
            bind:this={scannerRef}
            on:scan={async (e) => {
                const result = await handleBarcodeScan(e.detail);
                if (result.success) toast.success(result.message);
                scannerRef?.reportResult(result.success, result.message);
            }}
            on:close={() => isScanning = false}
        />
    {/if}
</div>

<style>
    .page-container {
        display: flex;
        flex-direction: column;
        width: 100vw;
        height: 100vh;
        background: #f1f5f9;
        overflow: hidden;
    }
    .content-wrapper {
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        position: relative;
    }
    .list-container-wrapper, .detail-container-wrapper {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        background: #f1f5f9;
        z-index: 1;
    }
    .detail-container-wrapper {
        z-index: 10;
    }
    .main-layout {
        flex: 1;
        display: flex;
        flex-direction: column;
        padding: 10px;
        gap: 10px;
        overflow: hidden;
    }
</style>
